import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import React, { useEffect, useMemo, useState } from 'react';
import zeplinColor from 'theme/Color';
import Profile from './Profile';
import DemographicTable, { SectionData } from './DemographicTable';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchDemographicProData } from 'store/slices/leadsSlice';
import { RowData } from './DemographicTable/DemographicTable';

const DemographicPro = () => {
  const dispatch = useAppDispatch();
  const { demographicData, loading, addLeadDraft } = useAppSelector((state) => state.leads);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<string>('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const tabLabels = [
    'Profile',
    'Basics',
    'Locations',
    'Education',
    'Work',
    'Likes',
    'Influencers',
    'Music',
    'Media',
    'Sports',
    'Brands',
  ];

  const Tabs = tabLabels.map((label, index) => ({
    label,
    id: index + 1,
    isHidden: tabValue !== index,
    Component: <></>,
  }));

  useEffect(() => {
    const id = addLeadDraft?.selectedIntegrations?.captiv8?.[0]?.Id;

    if (id) {
      dispatch(fetchDemographicProData(id))
        .unwrap()
        .then((data) => {
          setSelectedAccount(Object.keys(data)[0] || '');
        });
    }
  }, [addLeadDraft]);

  const accountData = demographicData?.[selectedAccount] || {};

  const toNumber = (value: unknown) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const toLabel = (row: any) =>
    row?.label ??
    row?.name ??
    row?.title ??
    row?.city ??
    row?.state ??
    row?.country ??
    row?.value ??
    row?.tag ??
    row?.handle ??
    'Unknown';

  const toPercentage = (row: any) =>
    toNumber(row?.percentage ?? row?.percent ?? row?.share ?? row?.value ?? row?.score ?? 0);

  const toRows = (rows: any[] = []): RowData[] =>
    rows.map((row) => {
      const percentage = toPercentage(row);
      return {
        label: toLabel(row),
        size: percentage,
        followers: percentage,
        instagramDistribution: percentage,
        avg: toNumber(row?.avg ?? row?.affinity ?? row?.index ?? row?.score ?? 0),
        from: row?.from ?? row?.countryCode ?? row?.country ?? '',
        percentage,
      };
    });

  const makeSection = (title: string, columnLabel: string, rows: any[] = []): SectionData => ({
    title,
    columnLabel,
    rows: toRows(rows),
  });

  const tabSections = useMemo<Record<number, SectionData[]>>(
    () => ({
      1: [
        makeSection('Gender', 'Gender', accountData?.basics?.gender || []),
        makeSection('Family Status', 'Family Status', accountData?.basics?.familyStatus || []),
        makeSection('Age', 'Age', accountData?.basics?.age || []),
      ],

      // Locations (sub-categorised)
      2: [
        makeSection('Top Cities', 'Top Cities', accountData?.location?.topCities || []),
        makeSection('Top Countries', 'Top Countries', accountData?.location?.topCountries || []),
        makeSection('Top States', 'Top States', accountData?.location?.topStates || []),
      ],

      3: [
        makeSection('Education Status', 'Education', accountData?.education?.status || []),
        makeSection('Organizations', 'Organizations', accountData?.education?.organizations || []),
      ],
      4: [
        makeSection('Occupations', 'Occupations', accountData?.work?.occupations || []),
        makeSection('Employers', 'Employers', accountData?.work?.employers || []),
        makeSection('Industries', 'Industries', accountData?.work?.industries || []),
      ],
      5: [makeSection('Likes', 'Likes', accountData?.likes || [])],
      6: [makeSection('Influencers', 'Influencers', accountData?.influencers || [])],

      // Music (sub-categorised)
      7: [
        makeSection('Bands', 'Bands', accountData?.music?.bands || []),
        makeSection('Genres', 'Genres', accountData?.music?.genres || []),
        makeSection('Solo Artists', 'Solo Artists', accountData?.music?.soloArtists || []),
      ],

      8: [makeSection('Media', 'Media', accountData?.media || [])],
      9: [
        makeSection('Liked Sports', 'LikedSports', accountData?.sports?.liked || []),
        makeSection('Teams', 'Teams', accountData?.sports?.teams || []),
      ],
      10: [makeSection('Brands', 'Brands', accountData?.brands || [])],
    }),
    [accountData],
  );

  return (
    <Box sx={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {' '}
          <FormControl
            sx={{
              '.MuiInputBase-formControl': {
                borderRadius: '50px',
                width: '154px',
              },
              minWidth: '154px',
              height: '48px',
            }}
          >
            <InputLabel
              id='pipeline-type-label'
              sx={{
                fontSize: { xs: '14px', xl: '16px' },
                '&.Mui-focused': { color: zeplinColor.BasicBlack },
              }}
            >
              Select account
            </InputLabel>
            <Select
              labelId='pipeline-type-label'
              id='pipeline-type'
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  color: zeplinColor.Neutral6,
                  border: `1px solid ${zeplinColor.Neutral6}`,
                  borderRadius: '50px',
                },

                height: '48px',
              }}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      color: zeplinColor.Neutral6,
                      border: `1px solid ${zeplinColor.Neutral6}`,
                    },
                    borderRadius: '50px',
                    border: `1px solid ${zeplinColor.Neutral6}`,
                    height: '48px',
                    padding: '0px 14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  label='Select account'
                />
              }
              renderValue={(selected) => selected as string}
              IconComponent={() => (
                <img src='/assets/down-arrow.svg' style={{ width: '24px', height: '24px' }} />
              )}
            >
              {Object.keys(demographicData || {}).map((label) => (
                <MenuItem
                  key={label}
                  value={label}
                  sx={{
                    fontSize: { xs: '14px', xl: '16px' },
                    padding: { xs: '6px 16px', xl: '8px 16px' },
                  }}
                >
                  <Checkbox
                    checked={selectedAccount === label}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': { color: zeplinColor.BasicBlack },
                    }}
                  />
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            height: '50px',
            width: '1px',
            backgroundColor: zeplinColor.Neutral5,
          }}
        />
        <CustomTab tabs={Tabs} handleChange={handleTabChange} value={tabValue} />
      </Box>
      {!loading && selectedAccount && (
        <Box>
          {tabValue === 0 && (
            <Profile setTabValue={setTabValue} value={demographicData?.[selectedAccount] || {}} />
          )}
          {tabValue >= 1 && tabValue <= 10 && (
            <DemographicTable sections={tabSections[tabValue] || []} />
          )}
        </Box>
      )}
      {demographicData && Object?.keys(demographicData)?.length === 0 ? (
        <Typography
          variant='responsiveBody2'
          color={zeplinColor.Neutral8}
          sx={{
            textAlign: 'center',
            height: 'calc(100vh - 400px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          No demographic data available for the selected account.
        </Typography>
      ) : null}
    </Box>
  );
};

export default DemographicPro;
